import { Location } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  ProviderToken,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { connectState } from '@examdojo/angular/util';
import { LoaderComponent } from '@examdojo/core/loader';
import { ToastService } from '@examdojo/core/toast';
import { ErrorHandlerService } from '@examdojo/error-handling';
import { HomeworkActivityQuery, HomeworkActivityService } from '@examdojo/homework';
import { PlatformService } from '@examdojo/platform';
import {
  FetchAttemptWhenMismatchEffectService,
  provideLocalMcqSelection,
  provideSingleAttemptSelfGradingSelection,
  QUESTION_CONTEXT,
  QUESTION_PROGRESS_DATA_SOURCE,
  QuestionAttemptEntityQuery,
  QuestionAttemptEntityService,
  QuestionAttemptRealtimeService,
  QuestionFooterDesktopComponent,
  QuestionFooterMobileComponent,
  QuestionGradingRealtimeService,
  removeSolutionCaptureQueryParamWithoutReloadState,
  SingleAttemptSelfGradingSelectionService,
} from '@examdojo/question';
import { SolutionCaptureQueryParam } from '@examdojo/solution';
import { isNotNullish } from '@examdojo/util/nullish';
import { IonContent } from '@ionic/angular/standalone';
import { TranslocoService } from '@jsverse/transloco';
import {
  combineLatest,
  distinctUntilChanged,
  EMPTY,
  filter,
  finalize,
  forkJoin,
  map,
  merge,
  of,
  startWith,
  switchMap,
  take,
  tap,
} from 'rxjs';
import {
  ExamdojoQuestionComponent,
  PostActivityScreensService,
  QuestionAttemptFullscreenFlowService,
} from '../../../question-v2';
import { CloseSolutionCaptureDialogHomeworkEffectService } from '../effects/close-solution-capture-dialog-homework-effect.service';
import { provideHomeworkActivityMismatchEffect } from '../effects/homework-activity-mismatch.adapter';
import { SyncProgressAfterGradingEffect } from '../effects/sync-progress-after-grading.effect';
import { HomeworkQuestionProgressDataSource } from '../homework-question-progress-data-source.service';
import { provideFullscreenFlowConfig } from './provide-fullscreen-flow-config';

@Component({
  selector: 'dojo-homework-activity-dialog-container',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './homework-activity-dialog-container.component.html',
  imports: [
    IonContent,
    ExamdojoQuestionComponent,
    LoaderComponent,
    QuestionFooterMobileComponent,
    QuestionFooterDesktopComponent,
  ],
  host: { class: 'flex flex-col h-full safe-area-top safe-area-bottom' },
  providers: [
    ...provideFullscreenFlowConfig(),
    PostActivityScreensService,
    QuestionGradingRealtimeService,
    QuestionAttemptRealtimeService,
    QuestionAttemptFullscreenFlowService,
    CloseSolutionCaptureDialogHomeworkEffectService,
    ...provideHomeworkActivityMismatchEffect(),
    provideLocalMcqSelection(),
    provideSingleAttemptSelfGradingSelection(),
    SyncProgressAfterGradingEffect,
    {
      provide: QUESTION_CONTEXT,
      useValue: { showHints: true, optionsSelectable: true, revealSolutionOnWrongAnswer: false },
    },
    {
      provide: QUESTION_PROGRESS_DATA_SOURCE,
      useClass: HomeworkQuestionProgressDataSource,
    },
  ],
})
export class HomeworkActivityDialogContainerComponent {
  constructor(
    private readonly activityQuery: HomeworkActivityQuery,
    private readonly activityService: HomeworkActivityService,
    private readonly questionAttemptQuery: QuestionAttemptEntityQuery,
    private readonly questionAttemptEntityService: QuestionAttemptEntityService,
    private readonly route: ActivatedRoute,
    private readonly location: Location,
    private readonly errorHandler: ErrorHandlerService,
    private readonly destroyRef: DestroyRef,
    private readonly platformService: PlatformService,
    private readonly toastService: ToastService,
    private readonly translocoService: TranslocoService,
  ) {
    merge(this.fetchQuestionAttemptOnActiveActivityChange(), this.setSolutionCaptureQueryParamWithoutReload())
      .pipe(takeUntilDestroyed())
      .subscribe();
  }

  private readonly instantiatedServices = [
    QuestionGradingRealtimeService,
    QuestionAttemptRealtimeService,
    QuestionAttemptFullscreenFlowService,
    FetchAttemptWhenMismatchEffectService,
    SingleAttemptSelfGradingSelectionService,
    SyncProgressAfterGradingEffect,
    CloseSolutionCaptureDialogHomeworkEffectService,
  ].map((service: ProviderToken<unknown>) => inject(service));

  readonly questionComponent = viewChild(ExamdojoQuestionComponent);
  readonly isDesktop = this.platformService.isDesktop;
  readonly isCanvasVisible = computed(() => this.questionComponent()?.isCanvasVisible() ?? false);

  private readonly isLoaded$ = combineLatest([
    this.questionAttemptQuery.questionLoaded$,
    this.activityQuery.selectLoading(),
  ]).pipe(
    map(([questionLoaded, loading]) => questionLoaded && !loading),
    distinctUntilChanged(),
  );

  readonly state = connectState({
    activeActivityId: this.activityQuery.ongoingActivityId$,
    activeActivity: this.activityQuery.ongoingActivity$,
    isLoading: this.isLoaded$.pipe(
      map((loaded) => !loaded),
      startWith(true),
    ),
    isSmall: this.platformService.isSmall$,
    isTransitioningBetweenQuestions: of(false),
    areAllQuestionsCompleted: this.activityQuery.areAllQuestionsCompleted$,
  });

  handleCompletedQuestion(action: 'continue' | 'stop') {
    const activityId = this.state.activeActivityId;

    if (!activityId) {
      return;
    }

    if (action === 'stop') {
      this.activityService
        .updateStatus(activityId, 'PAUSED')
        .pipe(
          tap(() => this.toastService.snackbar(this.translocoService.translate('progress_saved'))),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
      return;
    }

    if (this.state.areAllQuestionsCompleted) {
      this.activityService
        .completeActivityWithStatus(activityId, 'COMPLETED')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe();
      return;
    }

    this.continueToNextQuestion(activityId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  private setSolutionCaptureQueryParamWithoutReload() {
    return this.route.queryParamMap.pipe(
      switchMap((params) => {
        if (!params.get(SolutionCaptureQueryParam)) {
          return EMPTY;
        }

        return this.questionAttemptQuery.active$.pipe(
          filter(Boolean),
          take(1),
          tap(() => {
            this.questionAttemptEntityService.setOpenCaptureDialogFlag(true);
            this.location.replaceState(removeSolutionCaptureQueryParamWithoutReloadState());
          }),
        );
      }),
    );
  }

  private continueToNextQuestion(activityId: string) {
    return this.activityService.getNextQuestionAttempt(activityId).pipe(
      switchMap((nextAttemptId) => {
        if (!nextAttemptId) {
          return this.activityService.completeActivityWithStatus(activityId, 'COMPLETED');
        }

        return this.activityService.fetchQuestionAttempts(nextAttemptId, activityId, { resetStore: true });
      }),
      this.errorHandler.catchHttpErrors(() => EMPTY),
    );
  }

  private fetchQuestionAttemptOnActiveActivityChange() {
    return this.activityQuery.ongoingActivityId$.pipe(
      filter(isNotNullish),
      distinctUntilChanged(),
      switchMap((activityId) => {
        const attemptId = this.activityQuery.getEntity(activityId)?.question_attempt_id;
        if (!attemptId) {
          return EMPTY;
        }

        this.activityService.setLoading(true);

        return forkJoin([
          this.activityService.fetchQuestionAttempts(attemptId, activityId, { resetStore: true }),
          this.activityService.fetchProgress(activityId),
        ]).pipe(finalize(() => this.activityService.setLoading(false)));
      }),
      this.errorHandler.catchError(
        '[HomeworkActivityDialogContainerComponent.fetchQuestionAttemptOnActiveActivityChange]: failed to fetch question attempt details',
        () => EMPTY,
        {
          toast: (err) => ({
            title: 'An error occurred while loading the question',
            description: (err as Error)?.message,
          }),
        },
      ),
    );
  }
}
